import { Injectable } from '@angular/core';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { flatten } from 'lodash-es';
import { forkJoin, from, Observable, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { IGraphEntityRequest } from '../models/graph-entity-request';
import { GraphApiClient } from './graph.api-client';

@Injectable({ providedIn: 'root' })
export class GraphUserApiClient {
  public constructor(private readonly graphApiClient: GraphApiClient) {}

  public getUserLicense(): Observable<MicrosoftGraph.LicenseDetails[]> {
    return this.graphApiClient.getApiClientWithPermissions().pipe(
      switchMap((client) => {
        const query = client.api('/me/licenseDetails');

        return from(query.get()).pipe(map((result) => result.value));
      }),
    );
  }

  public getUserLicenses(ids: string[]): Observable<IMap<MicrosoftGraph.LicenseDetails[]>> {
    const licenseBatchLimit = 20;
    const graphRequests = ids.map(
      (id) =>
        new IGraphEntityRequest<{ value: MicrosoftGraph.LicenseDetails[] }, MicrosoftGraph.LicenseDetails[]>(
          (data) => data.value,
          `${id}/licenseDetails`,
          'users',
        ),
    );

    const slicedRequests = new Array(Math.ceil(graphRequests.length / licenseBatchLimit))
      .fill(null)
      .map((d) => graphRequests.splice(0, licenseBatchLimit));

    return ids.length !== 0
      ? forkJoin(slicedRequests.map((r) => this.graphApiClient.getGraphEntityByIds(r))).pipe(
          map((results) => {
            const licenses = flatten(results);
            return ids.reduce(
              (acc, curr, index) => {
                acc[curr] = licenses[index];
                return acc;
              },
              {} as IMap<MicrosoftGraph.LicenseDetails[]>,
            );
          }),
        )
      : of({});
  }
}
